/**
 * ONE TAP — shared state and wire protocol types.
 *
 * This is the seam between every subsystem: the simulation, the Durable Object,
 * the network layer, and the renderer all speak exactly these shapes. Changing
 * anything here is a cross-cutting change — flag it, don't do it quietly.
 */

import type { Vec3 } from './math';

// ---------------------------------------------------------------------------
// Enums / discriminants
// ---------------------------------------------------------------------------

export const enum MoveState {
  Grounded = 0,
  Airborne = 1,
  Sliding = 2,
  WallRunning = 3,
  Grappling = 4,
  Dashing = 5,
}

export const enum HealthState {
  Healthy = 0,
  Wounded = 1,
  Dead = 2,
}

export const enum GunState {
  /** Held by a player, round chambered. Glows hot yellow, hums. */
  HeldChambered = 0,
  /** Held by a player, empty. Dead grey, silent. */
  HeldEmpty = 1,
  /** Airborne — thrown, knocked loose, or dropped. Catchable. */
  InFlight = 2,
  /** Settled on the ground. Pickup-able, but pickup does NOT chamber. */
  Grounded = 3,
  /** Fell out of the world; waiting to respawn. */
  Void = 4,
}

export const enum RoundPhase {
  /** Waiting for enough players to start. */
  Lobby = 0,
  /** Frozen at spawn, countdown running. */
  Warmup = 1,
  Live = 2,
  /** Round decided; showing the outcome. */
  RoundEnd = 3,
  MatchEnd = 4,
}

export const enum HitZone {
  Head = 0,
  Body = 1,
  None = 2,
}

export const enum SurfaceKind {
  Concrete = 0,
  Metal = 1,
  Grate = 2,
  Glass = 3,
  Neon = 4,
}

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

/** Bitfield of button state, packed into one number on the wire. */
export const enum Btn {
  Jump = 1 << 0,
  Crouch = 1 << 1,
  Dash = 1 << 2,
  Fire = 1 << 3,
  Throw = 1 << 4,
  Grapple = 1 << 5,
  Pulse = 1 << 6,
  /** Held during a catch to refuse the heal and keep the chamber (see DESIGN.md §1). */
  CatchRaw = 1 << 7,
  Interact = 1 << 8,
}

/**
 * One tick of player intent. The client sends these; the server replays them.
 * Both sides must run the identical `stepMovement` over this — that is what makes
 * prediction and reconciliation agree.
 */
export interface InputCommand {
  /** Monotonic per-client sequence number. Used to ack and to replay. */
  seq: number;
  /** Fixed step this input covers. Normally TICK_DT. */
  dt: number;
  /** Movement intent in local space, each in [-1, 1]. x = strafe, z = forward. */
  moveX: number;
  moveZ: number;
  yaw: number;
  pitch: number;
  buttons: number;
  /**
   * The render time this input was aimed at, expressed on the **server's wall clock**
   * (`Connection.serverNowMs() - INTERP_DELAY_MS`), NOT on the server's world clock and
   * NOT on the raw `performance.now()` of the client.
   *
   * Two consumers depend on this and they disagreed until it was pinned down here:
   *  - `worker/room.ts::updateLatency` differences it against wall-clock `Date.now()` to
   *    derive the ping every player reads in the lobby. Wall clock is the only choice
   *    that makes that number honest.
   *  - `sim/gun.ts::computeRewindMs` prefers `w.timeMs - clientTimeMs` for lag-compensated
   *    hit rewind, but only when that lands in a plausible window; a wall-clock stamp does
   *    not, so it falls back to `latencyMs + INTERP_DELAY_MS`. Both paths are clamped to
   *    MAX_REWIND_MS inside `traceShot`, so neither can hand out an unbounded advantage.
   */
  clientTimeMs: number;
}

export const hasBtn = (buttons: number, b: Btn): boolean => (buttons & b) !== 0;

// ---------------------------------------------------------------------------
// Entity state
// ---------------------------------------------------------------------------

export interface PlayerState {
  id: string;
  name: string;
  team: number;
  bot: boolean;

  pos: Vec3;
  vel: Vec3;
  yaw: number;
  pitch: number;

  moveState: MoveState;
  healthState: HealthState;
  health: number;

  grounded: boolean;
  crouching: boolean;
  /** Surface normal of the wall currently being run on; null when not wall-running. */
  wallNormal: Vec3 | null;
  /** Identity of the wall surface, for the same-surface re-attach lockout. */
  wallSurfaceId: number;
  wallRunElapsedMs: number;

  dashCharges: number;
  dashCooldownMs: number;
  dashElapsedMs: number;
  dashDir: Vec3;

  slideElapsedMs: number;
  jumpsRemaining: number;
  coyoteMs: number;
  jumpBufferMs: number;

  grappleAnchorId: number; // -1 when not grappling
  grapplePoint: Vec3 | null;
  grappleLength: number;
  grappleElapsedMs: number;
  grappleCooldownMs: number;
  /** Hook is in flight toward the anchor but hasn't attached yet. */
  grappleHookProgress: number;

  /** Index into the match's gun array, or -1. */
  heldGun: number;
  fireCooldownMs: number;
  throwChargeMs: number;
  catchLockoutMs: number;
  pulseCooldownMs: number;

  kills: number;
  deaths: number;
  /** Successful mid-air catches — the stat that actually measures teamwork. */
  catches: number;
  /** Enemy passes intercepted. */
  intercepts: number;

  connected: boolean;
  latencyMs: number;
}

export interface GunEntity {
  id: number;
  /** Which team this gun belongs to. Re-tints on pickup by the other team. */
  team: number;
  state: GunState;
  /** Player id holding it, or null. */
  holder: string | null;
  /** Set on throw; used for THROW_SELF_CATCH_LOCKOUT_MS. */
  lastThrower: string | null;
  throwLockoutMs: number;

  /** Time spent InFlight since leaving the hands, and where it left from. Drives the in-flight
   *  trail, the incoming-pass HUD cue, and the self-catch lockout. Reset on throw/drop/disarm.
   *  (Catch eligibility itself is governed by CATCH_VIEW_CONE, not by distance or airtime.) */
  airborneMs: number;
  throwOrigin: Vec3 | null;

  pos: Vec3;
  vel: Vec3;
  /** Visual spin only — does not affect physics. */
  spin: Vec3;
  rot: Vec3;

  chambered: boolean;
  /** Time since last touched, for the abandoned-gun reset. */
  idleMs: number;
  voidRespawnMs: number;
}

export interface GrappleAnchor {
  id: number;
  pos: Vec3;
}

// ---------------------------------------------------------------------------
// Arena geometry — shared so server collision and client mesh come from ONE source.
// ---------------------------------------------------------------------------

/** An axis-aligned or yaw-rotated box brush. The arena is built entirely from these. */
export interface Brush {
  id: number;
  /** Center position. */
  pos: Vec3;
  /** Half-extents. */
  half: Vec3;
  /** Yaw rotation in radians. Only yaw — keeps collision cheap and exact. */
  yaw: number;
  surface: SurfaceKind;
  /** Marks a runnable wall for wall-run detection. */
  wallrunnable: boolean;
  /** Purely decorative; skipped by collision. */
  decorative?: boolean;
}

/** A ramp/wedge, for surf slopes. Normal points up the slope. */
export interface Ramp {
  id: number;
  pos: Vec3;
  half: Vec3;
  yaw: number;
  /** Slope angle in radians. */
  pitch: number;
  surface: SurfaceKind;
}

export interface ArenaData {
  name: string;
  brushes: Brush[];
  ramps: Ramp[];
  anchors: GrappleAnchor[];
  spawnsA: Vec3[];
  spawnsB: Vec3[];
  gunSpawnA: Vec3;
  gunSpawnB: Vec3;
  centerSpawn: Vec3;
  /** Below this Y a player or gun has fallen out of the world. */
  killPlaneY: number;
  bounds: { min: Vec3; max: Vec3 };
}

// ---------------------------------------------------------------------------
// Collision query interface.
//
// This is the seam that lets the collision agent and the movement agent work in
// parallel: movement code depends only on this interface, never on the implementation.
// ---------------------------------------------------------------------------

export interface TraceResult {
  /** Fraction of the requested motion completed, in [0,1]. */
  fraction: number;
  hit: boolean;
  normal: Vec3;
  point: Vec3;
  surfaceId: number;
  surface: SurfaceKind;
  /** True when the trace started already inside geometry. */
  startSolid: boolean;
}

export interface RaycastResult {
  hit: boolean;
  distance: number;
  point: Vec3;
  normal: Vec3;
  surfaceId: number;
  surface: SurfaceKind;
}

export interface CollisionWorld {
  /** Sweep a vertical capsule from `start` to `end`. The core of all movement. */
  traceCapsule(start: Vec3, end: Vec3, radius: number, height: number, out?: TraceResult): TraceResult;
  /** Sweep a sphere — used for the thrown gun. */
  traceSphere(start: Vec3, end: Vec3, radius: number, out?: TraceResult): TraceResult;
  /** Hitscan against world geometry only (players are tested separately, with rewind). */
  raycast(origin: Vec3, dir: Vec3, maxDist: number, out?: RaycastResult): RaycastResult;
  /** True if a capsule at this position overlaps geometry. */
  isSolid(pos: Vec3, radius: number, height: number): boolean;
  arena: ArenaData;
}

// ---------------------------------------------------------------------------
// Match state
// ---------------------------------------------------------------------------

export interface MatchState {
  phase: RoundPhase;
  round: number;
  scoreA: number;
  scoreB: number;
  phaseEndsAtMs: number;
  roundEndsAtMs: number;
  suddenDeath: boolean;
  /** Team that won the last round; -1 for none/draw. */
  lastRoundWinner: number;
  winner: number;
}

export interface WorldState {
  tick: number;
  timeMs: number;
  players: Record<string, PlayerState>;
  guns: GunEntity[];
  match: MatchState;
}

// ---------------------------------------------------------------------------
// Gameplay events — one-shot things the client turns into VFX/SFX/UI.
// The sim emits these; the renderer and audio engine consume them. Every event
// must produce BOTH a visual and an audible response (DESIGN.md §7).
// ---------------------------------------------------------------------------

export type GameEvent =
  | { t: 'shot'; player: string; origin: Vec3; dir: Vec3; hitPoint: Vec3; hitPlayer: string | null; zone: HitZone }
  | { t: 'dryfire'; player: string }
  | { t: 'kill'; killer: string; victim: string; zone: HitZone; gunId: number }
  | { t: 'wound'; victim: string; attacker: string }
  | { t: 'heal'; player: string; gunId: number }
  | { t: 'throw'; player: string; gunId: number; charged: boolean }
  | { t: 'catch'; player: string; gunId: number; chambered: boolean; raw: boolean; intercept: boolean }
  | { t: 'pickup'; player: string; gunId: number }
  | { t: 'gunbounce'; gunId: number; pos: Vec3; speed: number; surface: SurfaceKind }
  | { t: 'gunreset'; gunId: number; reason: 'void' | 'abandoned' }
  | { t: 'pulse'; player: string; pos: Vec3; hitPlayers: string[]; hitGun: number }
  | { t: 'disarm'; player: string; gunId: number; by: string }
  | { t: 'jump'; player: string; double: boolean }
  | { t: 'land'; player: string; speed: number; surface: SurfaceKind }
  | { t: 'slidestart'; player: string }
  | { t: 'dash'; player: string; dir: Vec3 }
  | { t: 'wallrun'; player: string; start: boolean }
  | { t: 'walljump'; player: string }
  | { t: 'grapplefire'; player: string; anchor: number }
  | { t: 'grappleattach'; player: string; anchor: number }
  | { t: 'grapplerelease'; player: string; boosted: boolean }
  | { t: 'roundstart'; round: number }
  | { t: 'roundend'; winner: number; reason: 'elimination' | 'timeout' | 'draw' }
  | { t: 'suddendeath' }
  | { t: 'matchend'; winner: number }
  | { t: 'playerjoin'; player: string; name: string; team: number }
  | { t: 'playerleave'; player: string };

// ---------------------------------------------------------------------------
// Wire protocol
// ---------------------------------------------------------------------------

export interface LobbyPlayerInfo {
  id: string;
  name: string;
  team: number;
  ready: boolean;
  bot: boolean;
  latencyMs: number;
}

export interface LobbyInfo {
  code: string;
  host: string;
  teamSize: number;
  players: LobbyPlayerInfo[];
  fillWithBots: boolean;
  arena: string;
}

export type ClientMessage =
  | { t: 'hello'; name: string; protocol: number }
  | { t: 'input'; cmds: InputCommand[] }
  | { t: 'ready'; ready: boolean }
  | { t: 'switchteam'; team: number }
  | { t: 'setbots'; fill: boolean }
  | { t: 'startmatch' }
  | { t: 'chat'; text: string }
  | { t: 'ping'; sent: number };

export type ServerMessage =
  | { t: 'welcome'; id: string; lobby: LobbyInfo; arena: ArenaData; serverTimeMs: number }
  | { t: 'lobby'; lobby: LobbyInfo }
  | { t: 'snapshot'; tick: number; timeMs: number; ackSeq: number; state: SnapshotPayload }
  | { t: 'events'; events: GameEvent[] }
  | { t: 'pong'; sent: number; serverTimeMs: number }
  | { t: 'chat'; from: string; text: string }
  | { t: 'error'; code: string; message: string }
  | { t: 'kick'; reason: string };

/** Snapshots are deltas of WorldState; the client reconstructs by patching its last full state. */
export interface SnapshotPayload {
  players: Record<string, Partial<PlayerState>>;
  guns: Partial<GunEntity>[];
  match: Partial<MatchState>;
  /** Ids removed since the last snapshot. */
  removed?: string[];
  /** True when this is a keyframe rather than a delta. */
  full?: boolean;
}
